import { pipe } from 'fp-ts/function';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { mapRoutingToFormValues, RoutingRuleFormValues } from '@mews-commander/routing-rules';
import { ApplicationState, CommanderThunkDispatch } from '@mews-commander/types';
import { FeatureFlagService, LocalizedText, lt, showSuccessAlert, withFeatureFlagService } from '@mews/framework';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { withAsyncContainer } from '@mews-commander/ui';
import { InvalidateQueries, useInvalidateRepositoryQueries } from '@mews-commander/data-access';
import { getRoutingRule } from '@mews-commander/legacy-entities/src/ducks/routingRules';
import { getRoutingRuleAssignmentsByRuleId } from '@mews-commander/legacy-entities/src/ducks/routingRuleAssignments';
import { editRoutingRule } from '../actions';
import { getRoutingRuleFormattedMeta, hasFailed, isFetching } from '../selectors';
import { RoutingRuleForm } from './RoutingRuleForm';

interface StateProps {
    routingRuleMeta: $TSFixMe;
    initialValues: Partial<RoutingRuleFormValues>;
    enableReinitialize: boolean;
}

interface DispatchProps {
    onSubmit: (values: RoutingRuleFormValues) => void;
    submitButtonProps: $TSFixMe;
}

interface OwnProps {
    routingRuleId: string;
    enterpriseId: string;
    featureFlagService: FeatureFlagService;
    invalidateQuery: (queries: InvalidateQueries) => Promise<void>[];
}

const ConnectedRoutingRuleForm = connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    (state, { routingRuleId, enterpriseId }) => {
        const routingRuleMeta = getRoutingRuleFormattedMeta(state, { id: routingRuleId, enterpriseId });
        const routingRule = getRoutingRule(state, { id: routingRuleId, enterpriseId });
        const routingRuleAssignments = getRoutingRuleAssignmentsByRuleId(state, { routingRuleId, enterpriseId });
        return {
            routingRuleMeta,
            initialValues: routingRule != null ? mapRoutingToFormValues(routingRule, routingRuleAssignments) : {},
            enableReinitialize: true,
        };
    },
    (dispatch: CommanderThunkDispatch, { routingRuleId, enterpriseId, featureFlagService, invalidateQuery }) => ({
        onSubmit: async (values) => {
            const isNewRoutingRulesOnAddEnabled = featureFlagService.hasFlag(FeatureFlag.newRoutingRulesOnAddEnabled);

            await dispatch(editRoutingRule({ values, id: routingRuleId, enterpriseId })).then(() => {
                if (isNewRoutingRulesOnAddEnabled) {
                    void invalidateQuery({ routingRules: ['newGetAll'] });
                }
                return showSuccessAlert({ description: lt('SaveSuccessMessage') });
            });
        },
        submitButtonProps: {
            label: <LocalizedText textKey={TextKey.Save} />,
        },
    })
)(RoutingRuleForm);

const ConnectedRoutingRuleFormWithFFService = withFeatureFlagService(ConnectedRoutingRuleForm);
const RoutingRuleFormWrapper = ({ ...props }: { enterpriseId: string; form: string; routingRuleId: string }) => {
    const invalidateQuery = useInvalidateRepositoryQueries();
    return <ConnectedRoutingRuleFormWithFFService {...props} invalidateQuery={invalidateQuery} />;
};

export default pipe(
    RoutingRuleFormWrapper,
    withAsyncContainer,
    connect(
        createStructuredSelector({
            isFetching,
            hasFailed,
        })
    )
);
